import { useEffect, useState } from 'react';
import {
  applyColorScheme,
  applyThemeParams,
} from '../utils/applyObankTheme';
import { PRODUCT_NAME } from '../constants/brand';

const TELEGRAM_BOOTSTRAP_TIMEOUT_MS = 3500;
const TELEGRAM_BOOTSTRAP_POLL_MS = 50;
const EXPAND_EVENT = 'web_app_expand';
const FULLSCREEN_REQUEST_EVENT = 'web_app_request_fullscreen';
const FULLSCREEN_MIN_VERSION = 8;
const TELEGRAM_DESKTOP_PLATFORMS = new Set([
  'desktop',
  'linux',
  'macos',
  'tdesktop',
  'web',
  'weba',
  'webk',
  'windows',
]);
const TELEGRAM_MOBILE_PLATFORMS = new Set(['android', 'android_x', 'ios']);
const ORIENTATION_LOCK_EVENT = 'web_app_toggle_orientation_lock';
const ORIENTATION_LOCK_MIN_VERSION = 8;
const SWIPE_BEHAVIOR_EVENT = 'web_app_setup_swipe_behavior';
const BIOMETRY_GET_INFO_EVENT = 'web_app_biometry_get_info';
const BIOMETRY_REQUEST_ACCESS_EVENT = 'web_app_biometry_request_access';
const BIOMETRY_UPDATE_TOKEN_EVENT = 'web_app_biometry_update_token';
const BIOMETRY_OPEN_SETTINGS_EVENT = 'web_app_biometry_open_settings';
const BIOMETRY_INFO_RECEIVED_EVENT = 'biometry_info_received';
const BIOMETRY_TOKEN_UPDATED_EVENT = 'biometry_token_updated';
const BIOMETRIC_MANAGER_UPDATED_EVENT = 'biometricManagerUpdated';
const TELEGRAM_BRIDGE_EVENT_TIMEOUT_MS = 45000;
const BIOMETRIC_SETTINGS_POLL_INTERVAL_MS = 500;
const BIOMETRIC_SETTINGS_POLL_TIMEOUT_MS = 15000;
const BIOMETRIC_GET_INFO_TIMEOUT_MS = 1500;
const BIOMETRIC_TOKEN_UPDATE_TIMEOUT_MS = 20000;
let subscribedWebApp = null;
let initializedWebApp = null;
let initializedBiometricWebApp = null;
let telegramState = null;
let telegramPollIntervalId = null;
let biometricAuthRequestPromise = null;
let biometricTokenUpdatePromise = null;
let biometricTokenSavedOverride = null;
let layoutMetricsFrameId = null;
let pendingLayoutMetricsWebApp = null;
const telegramSubscribers = new Set();

function getWebApp() {
  return window.Telegram?.WebApp ?? null;
}

function showTelegramBiometricDebug() {
  return undefined;
}

function safeBiometricDebugValue(value) {
  if (value === null || typeof value === 'undefined') {
    return 'null';
  }

  try {
    return JSON.stringify(value).slice(0, 180);
  } catch {
    return String(value).slice(0, 180);
  }
}

function parseTelegramVersion(version) {
  const [major = 0, minor = 0] = String(version || '')
    .split('.')
    .map((part) => Number.parseInt(part, 10));

  return {
    major: Number.isFinite(major) ? major : 0,
    minor: Number.isFinite(minor) ? minor : 0,
  };
}

function isTelegramVersionAtLeast(version, targetMajor, targetMinor = 0) {
  const { major, minor } = parseTelegramVersion(version);

  return major > targetMajor || (major === targetMajor && minor >= targetMinor);
}

function getSearchValue(source, key) {
  if (!source) {
    return '';
  }

  const normalizedSource = String(source).replace(/^[?#]/, '');
  const variants = [
    normalizedSource,
    normalizedSource.includes('?') ? normalizedSource.slice(normalizedSource.indexOf('?') + 1) : '',
  ].filter(Boolean);

  for (const variant of variants) {
    const value = new URLSearchParams(variant).get(key);

    if (value) {
      return value;
    }
  }

  const match = normalizedSource.match(new RegExp(`(?:^|[?#&])${key}=([^&#]+)`));

  return match ? decodeURIComponent(match[1]) : '';
}

function getLaunchInitData() {
  if (typeof window === 'undefined') {
    return '';
  }

  const sources = [
    window.location.search,
    window.location.hash,
    window.location.href,
  ].filter(Boolean);

  for (const source of sources) {
    const value = getSearchValue(source, 'tgWebAppData');

    if (value) {
      return value;
    }
  }

  return '';
}

function parseInitDataUser(initData) {
  if (!initData) {
    return null;
  }

  try {
    const rawUser = new URLSearchParams(initData).get('user');
    return rawUser ? JSON.parse(rawUser) : null;
  } catch {
    return null;
  }
}

function applyTelegramChrome(webApp) {
  if (!webApp) return;

  if (isTelegramVersionAtLeast(webApp.version, 6, 1)) {
    const backgroundColor = webApp.themeParams?.bg_color || webApp.themeParams?.secondary_bg_color;

    if (backgroundColor) {
      webApp.setHeaderColor?.(backgroundColor);
      webApp.setBackgroundColor?.(backgroundColor);
    }
  }
}

function getInset(insets, side) {
  const value = insets?.[side];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function setPixelVar(root, name, value) {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    const nextValue = `${value}px`;

    if (root.style.getPropertyValue(name) !== nextValue) {
      root.style.setProperty(name, nextValue);
    }
  }
}

function applyTelegramViewport(webApp) {
  if (!webApp || typeof document === 'undefined') return;

  const root = document.documentElement;
  setPixelVar(root, '--obank-viewport-height', webApp.viewportHeight);
  setPixelVar(root, '--obank-viewport-stable-height', webApp.viewportStableHeight);
}

function applyTelegramSafeArea(webApp) {
  if (!webApp || typeof document === 'undefined') return;

  const root = document.documentElement;

  ['top', 'right', 'bottom', 'left'].forEach((side) => {
    setPixelVar(root, `--obank-tg-safe-area-${side}`, getInset(webApp.safeAreaInset, side));
    setPixelVar(
      root,
      `--obank-tg-content-safe-area-${side}`,
      getInset(webApp.contentSafeAreaInset, side)
    );
  });
}

function applyTelegramLayoutMetrics(webApp) {
  applyTelegramViewport(webApp);
  applyTelegramSafeArea(webApp);
}

function scheduleTelegramLayoutMetrics(webApp) {
  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    applyTelegramLayoutMetrics(webApp);
    return;
  }

  pendingLayoutMetricsWebApp = webApp;

  if (layoutMetricsFrameId !== null) return;

  layoutMetricsFrameId = window.requestAnimationFrame(() => {
    const nextWebApp = pendingLayoutMetricsWebApp;
    layoutMetricsFrameId = null;
    pendingLayoutMetricsWebApp = null;
    applyTelegramLayoutMetrics(nextWebApp);
  });
}

function postTelegramBridgeEvent(eventType, eventData) {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof window.Telegram?.WebView?.postEvent === 'function') {
    if (typeof eventData === 'undefined') {
      window.Telegram.WebView.postEvent(eventType, false);
    } else {
      window.Telegram.WebView.postEvent(eventType, false, eventData);
    }
    return true;
  }

  if (typeof window.TelegramWebviewProxy?.postEvent === 'function') {
    window.TelegramWebviewProxy.postEvent(
      eventType,
      typeof eventData === 'undefined' ? undefined : JSON.stringify(eventData)
    );
    return true;
  }

  if (typeof window.external?.notify === 'function') {
    window.external.notify(JSON.stringify(
      typeof eventData === 'undefined'
        ? { eventType }
        : { eventType, eventData }
    ));
    return true;
  }

  if (window.parent && window.parent !== window && typeof window.parent.postMessage === 'function') {
    window.parent.postMessage(JSON.stringify(
      typeof eventData === 'undefined'
        ? { eventType }
        : { eventType, eventData }
    ), '*');
    return true;
  }

  return false;
}

function canPostTelegramBridgeEvent() {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(
    typeof window.Telegram?.WebView?.postEvent === 'function'
    || typeof window.TelegramWebviewProxy?.postEvent === 'function'
    || typeof window.external?.notify === 'function'
    || (window.parent && window.parent !== window && typeof window.parent.postMessage === 'function')
  );
}

function lockTelegramOrientation(webApp) {
  if (!webApp) return;

  try {
    if (
      typeof webApp.lockOrientation === 'function'
      && isTelegramVersionAtLeast(webApp.version, ORIENTATION_LOCK_MIN_VERSION)
    ) {
      webApp.lockOrientation();
      return;
    }
  } catch {
    // Fall through to the bridge event below.
  }

  postTelegramBridgeEvent(ORIENTATION_LOCK_EVENT, { locked: true });
}

function isDesktopTelegramPlatform(webApp) {
  const platform = String(webApp?.platform || '').trim().toLowerCase();

  if (TELEGRAM_MOBILE_PLATFORMS.has(platform)) {
    return false;
  }

  if (TELEGRAM_DESKTOP_PLATFORMS.has(platform)) {
    return true;
  }

  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';
  const isMobileUserAgent = /android|iphone|ipad|ipod|mobile/i.test(userAgent);

  if (isMobileUserAgent) {
    return false;
  }

  return /macintosh|windows|linux|x11/i.test(userAgent);
}

function requestTelegramFullsize(webApp) {
  if (!webApp) return;

  try {
    if (webApp.isExpanded === true) {
      return;
    }

    if (typeof webApp.expand === 'function') {
      webApp.expand();
      return;
    }
  } catch {
    // Fall through to the bridge event below.
  }

  postTelegramBridgeEvent(EXPAND_EVENT);
}

function requestTelegramFullscreen(webApp) {
  if (!webApp) return;

  let didRequestFullscreen = false;
  const isDesktop = isDesktopTelegramPlatform(webApp);
  const shouldRequestFullscreen = !isDesktop;

  if (isDesktop) {
    requestTelegramFullsize(webApp);
    return;
  }

  if (shouldRequestFullscreen) {
    try {
      if (webApp.isFullscreen === true) {
        didRequestFullscreen = true;
      } else if (
        typeof webApp.requestFullscreen === 'function'
        && isTelegramVersionAtLeast(webApp.version, FULLSCREEN_MIN_VERSION)
      ) {
        webApp.requestFullscreen();
        didRequestFullscreen = true;
      }
    } catch {
      didRequestFullscreen = false;
    }

    if (!didRequestFullscreen && isTelegramVersionAtLeast(webApp.version, FULLSCREEN_MIN_VERSION)) {
      didRequestFullscreen = postTelegramBridgeEvent(FULLSCREEN_REQUEST_EVENT);
    }
  }

  requestTelegramFullsize(webApp);
}

function disableTelegramClosingSwipes(webApp) {
  if (!webApp) return;

  try {
    if (typeof webApp.disableVerticalSwipes === 'function') {
      webApp.disableVerticalSwipes();
      return;
    }
  } catch {
    // Fall through to the bridge event below.
  }

  postTelegramBridgeEvent(SWIPE_BEHAVIOR_EVENT, { allow_vertical_swipe: false });
}

function getBiometricManager(webApp) {
  return webApp?.BiometricManager ?? null;
}

function buildBiometricState(webApp) {
  const manager = getBiometricManager(webApp);

  return {
    isInited: Boolean(manager?.isInited),
    isBiometricAvailable: Boolean(manager?.isBiometricAvailable),
    biometricType: manager?.biometricType || '',
    isAccessRequested: Boolean(manager?.isAccessRequested),
    isAccessGranted: Boolean(manager?.isAccessGranted),
    isBiometricTokenSaved: Boolean(manager?.isBiometricTokenSaved),
    deviceId: manager?.deviceId || '',
  };
}

function buildBiometricStateFromInfo(info = {}) {
  return {
    isInited: true,
    isBiometricAvailable: Boolean(info.available),
    biometricType: info.type || '',
    isAccessRequested: Boolean(info.access_requested),
    isAccessGranted: Boolean(info.access_granted),
    isBiometricTokenSaved: Boolean(info.token_saved),
    deviceId: info.device_id || '',
  };
}

function getCachedBiometricState(webApp) {
  return telegramState?.biometric || buildBiometricState(webApp);
}

function mergeBiometricState(previousBiometric, nextBiometric) {
  const shouldTrustNext = nextBiometric?.isInited === true;

  return {
    isInited: Boolean(nextBiometric?.isInited || previousBiometric?.isInited),
    isBiometricAvailable: shouldTrustNext
      ? Boolean(nextBiometric?.isBiometricAvailable)
      : Boolean(previousBiometric?.isBiometricAvailable),
    biometricType: nextBiometric?.biometricType || previousBiometric?.biometricType || '',
    isAccessRequested: shouldTrustNext
      ? Boolean(nextBiometric?.isAccessRequested)
      : Boolean(previousBiometric?.isAccessRequested),
    isAccessGranted: shouldTrustNext
      ? Boolean(nextBiometric?.isAccessGranted)
      : Boolean(previousBiometric?.isAccessGranted),
    isBiometricTokenSaved: typeof nextBiometric?.isBiometricTokenSaved === 'boolean'
      ? nextBiometric.isBiometricTokenSaved
      : Boolean(previousBiometric?.isBiometricTokenSaved),
    deviceId: nextBiometric?.deviceId || previousBiometric?.deviceId || '',
  };
}

function applyBiometricTokenSavedOverride(biometric) {
  if (typeof biometricTokenSavedOverride !== 'boolean') {
    return biometric;
  }

  return {
    ...biometric,
    isBiometricTokenSaved: biometricTokenSavedOverride,
  };
}

function refreshBiometricState(webApp, biometricOverride) {
  if (!telegramState) return;
  const nextBiometric = applyBiometricTokenSavedOverride(
    biometricOverride || buildBiometricState(webApp)
  );

  setTelegramState({
    ...telegramState,
    biometricManager: getBiometricManager(webApp),
    biometric: nextBiometric,
  });
}

function initializeTelegramBiometrics(webApp) {
  const biometricManager = getBiometricManager(webApp);

  if (!biometricManager || initializedBiometricWebApp === webApp) {
    return;
  }

  initializedBiometricWebApp = webApp;

  try {
    if (!biometricManager.isInited && typeof biometricManager.init === 'function') {
      biometricManager.init(() => refreshBiometricState(webApp));
      return;
    }
  } catch {
    // Keep the initial snapshot if Telegram refuses biometry init.
  }

  window.setTimeout(() => refreshBiometricState(webApp), 0);
}

function parseBridgeEventMessage(event) {
  const rawData = event?.data;

  if (!rawData) return null;

  if (typeof rawData === 'object') {
    return rawData;
  }

  if (typeof rawData !== 'string') {
    return null;
  }

  try {
    return JSON.parse(rawData);
  } catch {
    return null;
  }
}

function getBridgeEventType(data) {
  return data?.eventType || data?.event_type || data?.type;
}

function getBridgeEventData(data) {
  return data?.eventData || data?.event_data || data?.data || data;
}

function waitForTelegramBridgeEvent(eventType, timeoutMs = TELEGRAM_BRIDGE_EVENT_TIMEOUT_MS) {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let timeoutId = null;
    const telegramWebView = window.Telegram?.WebView;
    const telegramWebApp = window.Telegram?.WebApp;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener(eventType, handleCustomEvent);

      try {
        telegramWebView?.offEvent?.(eventType, handleTelegramEvent);
      } catch {
        // Telegram SDK availability differs by client/version.
      }

      try {
        telegramWebApp?.offEvent?.(eventType, handleTelegramEvent);
      } catch {
        // Telegram SDK availability differs by client/version.
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };

    const finish = (payload) => {
      cleanup();
      resolve(payload || null);
    };

    const handleMessage = (event) => {
      const data = parseBridgeEventMessage(event);

      if (getBridgeEventType(data) === eventType) {
        finish(getBridgeEventData(data));
      }
    };

    const handleCustomEvent = (event) => {
      finish(event?.detail || null);
    };

    const handleTelegramEvent = (...args) => {
      const payload = args.length > 1 ? args[1] : args[0];
      finish(getBridgeEventData(payload));
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener(eventType, handleCustomEvent);

    try {
      telegramWebView?.onEvent?.(eventType, handleTelegramEvent);
    } catch {
      // Telegram SDK availability differs by client/version.
    }

    try {
      telegramWebApp?.onEvent?.(eventType, handleTelegramEvent);
    } catch {
      // Telegram SDK availability differs by client/version.
    }

    timeoutId = window.setTimeout(() => finish(null), timeoutMs);
  });
}

function waitForTelegramWebAppEvent(eventType, timeoutMs = TELEGRAM_BRIDGE_EVENT_TIMEOUT_MS) {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let timeoutId = null;
    const telegramWebApp = window.Telegram?.WebApp;

    const cleanup = () => {
      window.removeEventListener(eventType, handleCustomEvent);

      try {
        telegramWebApp?.offEvent?.(eventType, handleTelegramEvent);
      } catch {
        // Telegram SDK availability differs by client/version.
      }

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };

    const finish = (payload) => {
      cleanup();
      resolve(payload || null);
    };

    const handleCustomEvent = (event) => finish(event?.detail || true);
    const handleTelegramEvent = (...args) => {
      const payload = args.length > 1 ? args[1] : args[0];
      finish(payload || true);
    };

    window.addEventListener(eventType, handleCustomEvent);

    try {
      telegramWebApp?.onEvent?.(eventType, handleTelegramEvent);
    } catch {
      // Telegram SDK availability differs by client/version.
    }

    timeoutId = window.setTimeout(() => finish(null), timeoutMs);
  });
}

function subscribeThemeChanges(webApp) {
  if (!webApp?.onEvent) return;
  if (subscribedWebApp === webApp) return;

  subscribedWebApp = webApp;

  webApp.onEvent('themeChanged', () => {
    applyThemeParams(webApp.themeParams);
    applyColorScheme(webApp.colorScheme);
    applyTelegramChrome(webApp);
  });
  webApp.onEvent('viewportChanged', () => {
    scheduleTelegramLayoutMetrics(webApp);
  });
  webApp.onEvent('safeAreaChanged', () => {
    scheduleTelegramLayoutMetrics(webApp);
  });
  webApp.onEvent('contentSafeAreaChanged', () => {
    scheduleTelegramLayoutMetrics(webApp);
  });
  webApp.onEvent('fullscreenChanged', () => {
    scheduleTelegramLayoutMetrics(webApp);
  });
  webApp.onEvent('fullscreenFailed', () => {
    try {
      webApp.expand?.();
    } catch {
      // Keep the current Telegram viewport if both fullscreen and expand fail.
    }
  });
}

function prepareTelegramWebApp(webApp) {
  if (!webApp || initializedWebApp === webApp) return;

  initializedWebApp = webApp;

  applyThemeParams(webApp.themeParams);
  applyColorScheme(webApp.colorScheme);
  applyTelegramChrome(webApp);
  applyTelegramLayoutMetrics(webApp);
  lockTelegramOrientation(webApp);
  disableTelegramClosingSwipes(webApp);
  subscribeThemeChanges(webApp);
  initializeTelegramBiometrics(webApp);

  webApp.ready?.();
  requestTelegramFullscreen(webApp);
}

function buildUnavailableTelegramState({ isReady }) {
  applyColorScheme('light');

  return {
    isReady,
    isMock: false,
    user: null,
    initData: '',
    webApp: null,
    biometricManager: null,
    biometric: buildBiometricState(null),
    colorScheme: 'light',
    error: isReady
      ? 'Telegram WebApp API недоступен. Запустите приложение через Telegram.'
      : null,
  };
}

function initializeTelegram({ isReady = true } = {}) {
  const webApp = getWebApp();
  const launchInitData = getLaunchInitData();

  if (!webApp) {
    if (!launchInitData) {
      return buildUnavailableTelegramState({ isReady });
    }

    return {
      isReady,
      isMock: false,
      user: parseInitDataUser(launchInitData),
      initData: launchInitData,
      webApp: null,
      biometricManager: null,
      biometric: buildBiometricState(null),
      colorScheme: 'light',
      error: null,
    };
  }

  prepareTelegramWebApp(webApp);

  const initData = webApp.initData || launchInitData;

  return {
    isReady,
    isMock: false,
    user: webApp.initDataUnsafe?.user ?? parseInitDataUser(initData),
    initData,
    webApp,
    biometricManager: getBiometricManager(webApp),
    biometric: buildBiometricState(webApp),
    colorScheme: webApp.colorScheme ?? 'light',
    error: null,
  };
}

function isTelegramReady(state) {
  return Boolean(state.initData);
}

function notifyTelegramSubscribers(nextState) {
  telegramSubscribers.forEach((subscriber) => subscriber(nextState));
}

function setTelegramState(nextState) {
  telegramState = nextState;

  notifyTelegramSubscribers(nextState);
}

function getTelegramState() {
  if (!telegramState) {
    const initialState = initializeTelegram({ isReady: false });
    telegramState = isTelegramReady(initialState)
      ? { ...initialState, isReady: true }
      : initialState;
  }

  return telegramState;
}

function stopTelegramPolling() {
  if (!telegramPollIntervalId) {
    return;
  }

  window.clearInterval(telegramPollIntervalId);
  telegramPollIntervalId = null;
}

function ensureTelegramPolling() {
  const currentState = getTelegramState();

  if (
    typeof window === 'undefined'
    || telegramPollIntervalId
    || currentState.isReady
    || isTelegramReady(currentState)
    || currentState.error
  ) {
    return;
  }

  const startedAt = Date.now();

  telegramPollIntervalId = window.setInterval(() => {
    const nextState = initializeTelegram({ isReady: false });

    if (isTelegramReady(nextState)) {
      stopTelegramPolling();
      setTelegramState({ ...nextState, isReady: true });
      return;
    }

    if (!nextState.initData && Date.now() - startedAt >= TELEGRAM_BOOTSTRAP_TIMEOUT_MS) {
      stopTelegramPolling();
      setTelegramState(initializeTelegram({ isReady: true }));
    }
  }, TELEGRAM_BOOTSTRAP_POLL_MS);
}

export function useTelegram() {
  const [state, setState] = useState(getTelegramState);

  useEffect(() => {
    telegramSubscribers.add(setState);
    ensureTelegramPolling();

    return () => {
      telegramSubscribers.delete(setState);

      if (telegramSubscribers.size === 0) {
        stopTelegramPolling();
      }
    };
  }, []);

  return state;
}

export function refreshTelegramBiometrics(webApp) {
  const biometric = applyBiometricTokenSavedOverride(
    mergeBiometricState(getCachedBiometricState(webApp), buildBiometricState(webApp))
  );
  refreshBiometricState(webApp, biometric);

  return biometric;
}

function initBiometricManagerFallback(webApp) {
  const biometricManager = getBiometricManager(webApp);

  if (!biometricManager || typeof biometricManager.init !== 'function') {
    return Promise.resolve(refreshTelegramBiometrics(webApp));
  }

  return new Promise((resolve) => {
    let isResolved = false;
    const finish = () => {
      if (isResolved) return;

      isResolved = true;
      resolve(refreshTelegramBiometrics(webApp));
    };

    try {
      biometricManager.init(finish);
      window.setTimeout(finish, BIOMETRIC_SETTINGS_POLL_INTERVAL_MS);
    } catch {
      finish();
    }
  });
}

export async function requestTelegramBiometricInfo(webApp, {
  timeoutMs = BIOMETRIC_GET_INFO_TIMEOUT_MS,
} = {}) {
  if (!canPostTelegramBridgeEvent()) {
    return initBiometricManagerFallback(webApp);
  }

  const infoPromise = waitForTelegramBridgeEvent(BIOMETRY_INFO_RECEIVED_EVENT, timeoutMs);
  const managerUpdatedPromise = waitForTelegramWebAppEvent(
    BIOMETRIC_MANAGER_UPDATED_EVENT,
    timeoutMs
  );
  const isPosted = postTelegramBridgeEvent(BIOMETRY_GET_INFO_EVENT);

  if (!isPosted) {
    return initBiometricManagerFallback(webApp);
  }

  const payload = await infoPromise;

  if (payload) {
    const biometric = applyBiometricTokenSavedOverride(buildBiometricStateFromInfo(payload));
    refreshBiometricState(webApp, biometric);

    return biometric;
  }

  await managerUpdatedPromise;
  const biometric = applyBiometricTokenSavedOverride(
    mergeBiometricState(getCachedBiometricState(webApp), buildBiometricState(webApp))
  );
  refreshBiometricState(webApp, biometric);

  return biometric;
}

export async function pollTelegramBiometricAccess(webApp, {
  intervalMs = BIOMETRIC_SETTINGS_POLL_INTERVAL_MS,
  timeoutMs = BIOMETRIC_SETTINGS_POLL_TIMEOUT_MS,
} = {}) {
  const startedAt = Date.now();
  let latestBiometric = refreshTelegramBiometrics(webApp);

  while (Date.now() - startedAt < timeoutMs) {
    latestBiometric = await requestTelegramBiometricInfo(webApp);

    if (latestBiometric?.isAccessGranted === true) {
      return latestBiometric;
    }

    await new Promise((resolve) => {
      window.setTimeout(resolve, intervalMs);
    });
  }

  return latestBiometric;
}

function normalizeBiometricAccessResult(result, webApp) {
  if (typeof result === 'boolean') {
    const biometric = {
      ...buildBiometricState(webApp),
      isAccessRequested: true,
      isAccessGranted: result,
    };

    return {
      accessGranted: result,
      biometric,
    };
  }

  const biometric = result
    ? buildBiometricStateFromInfo(result)
    : buildBiometricState(webApp);

  return {
    accessGranted: biometric.isAccessGranted,
    biometric,
  };
}

export function requestTelegramBiometricAccess(webApp, params = {}) {
  const biometricManager = getBiometricManager(webApp);
  const reason = params.reason || 'Enable biometric sign-in';

  if (biometricManager && typeof biometricManager.requestAccess === 'function') {
    return new Promise((resolve, reject) => {
      try {
        biometricManager.requestAccess({ reason }, (result) => {
          const normalizedResult = normalizeBiometricAccessResult(result, webApp);
          refreshBiometricState(webApp, normalizedResult.biometric);
          resolve(normalizedResult);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  const isPosted = postTelegramBridgeEvent(BIOMETRY_REQUEST_ACCESS_EVENT, { reason });

  return waitForTelegramBridgeEvent(BIOMETRY_INFO_RECEIVED_EVENT)
    .then((payload) => {
      const normalizedResult = normalizeBiometricAccessResult(payload, webApp);
      refreshBiometricState(webApp, normalizedResult.biometric);

      return {
        ...normalizedResult,
        isPosted,
      };
    });
}

function normalizeBiometricTokenUpdateResult(result, { isRemoving = false } = {}) {
  if (typeof result === 'boolean') {
    return {
      status: result ? 'updated' : isRemoving ? 'removed' : 'failed',
      isUpdated: result,
    };
  }

  if (typeof result?.isUpdated === 'boolean') {
    return {
      status: result.isUpdated ? 'updated' : isRemoving ? 'removed' : 'failed',
      isUpdated: result.isUpdated,
    };
  }

  const status = result?.status || 'failed';

  return {
    status,
    isUpdated: status === 'updated',
  };
}

function applyBiometricTokenUpdate(webApp, normalizedResult) {
  const cachedBiometric = getCachedBiometricState(webApp);
  const isUpdated = normalizedResult.status === 'removed'
    ? false
    : normalizedResult.status === 'failed'
    ? cachedBiometric.isBiometricTokenSaved
    : normalizedResult.isUpdated;

  if (normalizedResult.status !== 'failed') {
    biometricTokenSavedOverride = isUpdated;
  }

  showTelegramBiometricDebug(
    `apply token status=${normalizedResult.status || 'none'} updated=${normalizedResult.isUpdated ? '1' : '0'} cachedToken=${cachedBiometric.isBiometricTokenSaved ? '1' : '0'} nextToken=${isUpdated ? '1' : '0'}`
  );

  refreshBiometricState(webApp, {
    ...cachedBiometric,
    isAccessRequested: normalizedResult.isUpdated ? true : cachedBiometric.isAccessRequested,
    isAccessGranted: normalizedResult.isUpdated ? true : cachedBiometric.isAccessGranted,
    isBiometricTokenSaved: isUpdated,
  });
}

function updateTelegramBiometricTokenViaBridge(webApp, token) {
  if (!canPostTelegramBridgeEvent()) {
    showTelegramBiometricDebug('bridge update_token unavailable');
    return null;
  }

  const rawTokenUpdatePromise = waitForTelegramBridgeEvent(
    BIOMETRY_TOKEN_UPDATED_EVENT,
    BIOMETRIC_TOKEN_UPDATE_TIMEOUT_MS
  ).then((payload) => ({
    source: 'raw',
    payload,
  }));

  showTelegramBiometricDebug(`bridge update_token post start tokenLen=${String(token || '').length}`);
  const isPosted = postTelegramBridgeEvent(BIOMETRY_UPDATE_TOKEN_EVENT, { token });
  showTelegramBiometricDebug(`bridge update_token posted=${isPosted ? '1' : '0'}`);

  if (!isPosted) {
    return null;
  }

  return rawTokenUpdatePromise
    .then(({ source, payload }) => {
      showTelegramBiometricDebug(
        `bridge update_token event source=${source} payload=${safeBiometricDebugValue(payload)}`
      );
      const normalizedResult = normalizeBiometricTokenUpdateResult(payload, {
        isRemoving: !token,
      });
      applyBiometricTokenUpdate(webApp, normalizedResult);

      return {
        ...normalizedResult,
        isPosted,
      };
    });
}

export function updateTelegramBiometricToken(webApp, token) {
  const cachedBiometric = getCachedBiometricState(webApp);

  if (biometricTokenUpdatePromise) {
    showTelegramBiometricDebug('update_token skipped: already in progress');
    return biometricTokenUpdatePromise;
  }

  showTelegramBiometricDebug(
    `update_token path bridge=1 cachedGranted=${cachedBiometric?.isAccessGranted === true ? '1' : '0'} tokenLen=${String(token || '').length}`
  );

  biometricTokenUpdatePromise = (
    updateTelegramBiometricTokenViaBridge(webApp, token) || Promise.resolve({
      status: 'failed',
      isUpdated: false,
      isPosted: false,
    })
  ).finally(() => {
    biometricTokenUpdatePromise = null;
  });

  return biometricTokenUpdatePromise;
}

export function openTelegramBiometricSettings(webApp) {
  const biometricManager = getBiometricManager(webApp);

  try {
    if (biometricManager && typeof biometricManager.openSettings === 'function') {
      biometricManager.openSettings();
      window.setTimeout(() => refreshBiometricState(webApp), 500);
      return true;
    }
  } catch {
    // Fall through to the bridge event.
  }

  const isPosted = postTelegramBridgeEvent(BIOMETRY_OPEN_SETTINGS_EVENT);
  window.setTimeout(() => refreshBiometricState(webApp), 500);

  return isPosted;
}

function normalizeBiometricAuthResult(result, fallbackToken = '') {
  if (typeof result === 'boolean') {
    return {
      isAuthenticated: result,
      biometricToken: fallbackToken || '',
    };
  }

  if (typeof result?.isAuthenticated === 'boolean') {
    return {
      isAuthenticated: result.isAuthenticated,
      biometricToken: result?.biometricToken || result?.token || fallbackToken || '',
    };
  }

  const status = result?.status || 'failed';

  return {
    isAuthenticated: status === 'authorized',
    biometricToken: result?.token || result?.biometricToken || fallbackToken || '',
  };
}

function createTelegramBiometricAuthRequest(webApp, params = {}) {
  const biometricManager = getBiometricManager(webApp);
  const reason = params.reason || `Unlock ${PRODUCT_NAME}`;

  if (biometricManager && typeof biometricManager.authenticate === 'function') {
    return new Promise((resolve, reject) => {
      try {
        biometricManager.authenticate({ reason }, (isAuthenticated, biometricToken) => {
          refreshBiometricState(webApp);
          resolve(normalizeBiometricAuthResult(isAuthenticated, biometricToken));
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  return Promise.resolve({
    isAuthenticated: false,
    biometricToken: '',
    isPosted: false,
  });
}

export function requestTelegramBiometricAuth(webApp, params = {}) {
  if (biometricAuthRequestPromise) {
    return biometricAuthRequestPromise;
  }

  biometricAuthRequestPromise = createTelegramBiometricAuthRequest(webApp, params)
    .finally(() => {
      biometricAuthRequestPromise = null;
    });

  return biometricAuthRequestPromise;
}

export { applyColorScheme, applyThemeParams } from '../utils/applyObankTheme';
